import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  RefreshControl,
  Modal,
  Image,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { formatPct } from '../../utils/formatPercentage';
import Svg, { Path, Circle, G, Rect, Line } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { employeesApi, kpisApi, processesApi } from '../../services/apiService';
import { invalidate as invalidateData } from '../../utils/dataInvalidation';
import { Employee, KPIList, ProcessList } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useRoleNavigation, useResponsive } from '../../hooks';
import TargetAchievementCard from '../../components/TargetAchievementCard';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'EmployeeDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UserAvatarIcon = ({ size = 48 }: { size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <Circle cx="24" cy="24" r="23" fill={tokens.colors.raw.gray["100"]} stroke={tokens.colors.raw.gray["200"]} strokeWidth="2"/>
    <Path d="M24 26C28.4183 26 32 22.4183 32 18C32 13.5817 28.4183 10 24 10C19.5817 10 16 13.5817 16 18C16 22.4183 19.5817 26 24 26Z" fill={tokens.colors.raw.gray["300"]}/>
    <Path d="M10 40C10 33.3726 15.3726 28 22 28H26C32.6274 28 38 33.3726 38 40" fill={tokens.colors.raw.gray["300"]}/>
  </Svg>
);

// Circular Progress Component
const CircularProgress = ({ percentage, size = 100, showLabel = true }: { percentage: number; size?: number; showLabel?: boolean }) => {
  const strokeWidth = size > 80 ? 8 : 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  let progressColor: string = tokens.colors.raw.red["500"]; // Red - Needs Improvement (< 80%)
  if (percentage >= 100) progressColor = tokens.colors.raw.green["500"]; // Green - Excellent
  else if (percentage >= 80) progressColor = tokens.colors.raw.orange["500"]; // Orange - Good

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.gray["200"]}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      {showLabel && size > 80 && (
        <View style={{ position: 'absolute', alignItems: 'center' }}>
          <Text style={{ fontSize: tokens.typography["heading 3"]["font size"], fontWeight: '700', color: tokens.colors.raw.gray["800"] }}>{percentage} %</Text>
        </View>
      )}
    </View>
  );
};

// Progress Bar Component - RTL (fills from right to left)
const ProgressBar = ({ percentage, color = tokens.colors.raw.green["500"] }: { percentage: number; color?: string }) => (
  <View style={styles.progressBarContainer}>
    <View style={[styles.progressBarFill, { width: `${Math.min(percentage, 100)}%`, backgroundColor: color }]} />
    <View style={[styles.progressDot, { end: `${Math.min(percentage, 100)}%`, backgroundColor: color }]} />
  </View>
);

// ============================================================
// Info Row Component - Table style matching Figma
// ============================================================

interface InfoRowProps {
  label: string;
  value: string | number | undefined | null;
}

const InfoRow = ({ label, value }: InfoRowProps) => (
  <View style={styles.infoRow}>
    <View style={styles.infoLabelBox}>
      <Text style={styles.infoLabel}>{label}</Text>
    </View>
    <Text style={styles.infoValue}>{value || '-'}</Text>
  </View>
);

// Stat Row for إحصائيات الأداء
const StatRow = ({ label, value }: { label: string; value: string | number }) => (
  <View style={styles.statRow}>
    <Text style={styles.statValue}>{value}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </View>
);

// Small Circular Progress for percentage badge (donut style)
const SmallCircularProgress = ({ percentage, size = 32 }: { percentage: number; size?: number }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background circle */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke={tokens.colors.raw.gray["200"]}
        strokeWidth={strokeWidth}
        fill="transparent"
      />
      {/* Progress arc */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke={tokens.colors.raw.green["500"]}
        strokeWidth={strokeWidth}
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
      />
    </Svg>
  );
};

// Process Card Component
const ProcessCard = ({ name, percentage, onViewDetails, viewDetailsText }: { name: string; percentage: number; onViewDetails?: () => void; viewDetailsText: string }) => {
  const { colors, isDark } = useTheme();
  return (
    <View style={[styles.processCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <Text style={[styles.processCardTitle, { color: colors.textPrimary }]}>{name}</Text>
      <View style={styles.kpiProgressRow}>
        <View style={styles.kpiPercentageContainer}>
          <Text style={[styles.kpiPercentage, { color: colors.textPrimary }]}>{percentage} %</Text>
          <View style={styles.kpiDot} />
        </View>
        <ProgressBar percentage={percentage} />
      </View>
      <TouchableOpacity onPress={onViewDetails} style={styles.kpiDetailsLink}>
        <ChevronLeftIcon size={14} color={colors.textMuted} />
        <Text style={[styles.kpiDetailsText, { color: colors.textMuted }]}>{viewDetailsText}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Helper Functions
// ============================================================

const formatArabicDate = (dateString: string): string => {
  const arabicMonths = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const parts = dateString.split('-');
  if (parts.length === 3) {
    const year = parts[0];
    const month = parseInt(parts[1]) - 1;
    const day = parts[2];
    return `${day} ${arabicMonths[month]}، ${year}`;
  }
  return dateString;
};

// ============================================================
// Main Component
// ============================================================

export default function EmployeeDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id, source } = route.params as { id: number; source?: 'myorganization' | 'employees' };
  const isFromEmployees = source === 'employees';
  const { width } = useWindowDimensions();
  const { showNotification } = useNotification();
  const { navigateToHome } = useRoleNavigation();
  const { horizontalCardWidth } = useResponsive();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [processes, setProcesses] = useState<ProcessList[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);

  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1200;

  const fetchEmployee = useCallback(async () => {
    try {
      const data = await employeesApi.get(id);
      setEmployee(data);

      // Fetch KPIs linked to this employee
      setKpisLoading(true);
      try {
        console.log('Fetching KPIs for employee ID:', id);
        const kpisData = await kpisApi.list({ employee: id, page_size: 100 });
        console.log('KPIs fetched:', kpisData.results?.length || 0, kpisData.results);
        setKpis(kpisData.results || []);
      } catch (err) {
        console.error('Failed to fetch KPIs:', err);
      } finally {
        setKpisLoading(false);
      }

      // Fetch processes where employee is assigned to steps (via ManyToMany)
      setProcessesLoading(true);
      try {
        const processesData = await employeesApi.processes(id);
        console.log('Assigned processes fetched:', processesData.total_processes, processesData.processes);
        setProcesses(processesData.processes || []);
      } catch (err) {
        console.error('Failed to fetch assigned processes:', err);
        // Fallback: try to fetch processes by organizational unit if the new endpoint fails
        if (data.organizational_unit) {
          try {
            const fallbackData = await processesApi.list({
              organizational_unit: data.organizational_unit,
              page_size: 100
            });
            setProcesses(fallbackData.results || []);
          } catch (fallbackErr) {
            console.error('Fallback fetch also failed:', fallbackErr);
          }
        }
      } finally {
        setProcessesLoading(false);
      }
    } catch (err: any) {
      console.error('Failed to fetch employee:', err);
      showNotification(t('employees.loadFailed'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, showNotification]);

  useFocusEffect(
    useCallback(() => {
      fetchEmployee();
    }, [fetchEmployee])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchEmployee();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchEmployee();
  };

  const handleEdit = () => {
    navigation.navigate('AddEmployee', { employeeId: id });
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await employeesApi.delete(id);
      showNotification(t('employees.deleteSuccess'), 'success');
      invalidateData('my-organization');
      navigation.goBack();
    } catch (err: any) {
      console.error('Failed to delete employee:', err);
      showNotification(t('employees.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  if (!employee) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('employees.notFound')}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => navigation.goBack()}>
            <Text style={styles.retryButtonText}>{t('common.back')}</Text>
          </TouchableOpacity>
        </View>
      </PageContainer>
    );
  }

  // Use backend's weighted average calculation for overall achievement
  // Formula: sum(achievement × weight) / sum(weights)
  const overallPercentage = parseFloat(Number(employee.overall_achievement || 0).toFixed(2));

  // Use backend's total readings count
  const totalReadings = employee.readings_count || 0;

  // Get last reading value
  const lastReading = kpis.length > 0 && kpis[0].latest_value
    ? `${kpis[0].latest_value} ${kpis[0].unit || '%'}`
    : '-';

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home') },
            { label: isFromEmployees ? t('navigation.humanCapital') : t('navigation.myOrganization'), route: isFromEmployees ? 'Employees' : 'MyOrganization' },
            { label: t('employees.employeeDetails') },
          ]}
        />

        <PageHeader
          title={employee.name_ar}
          description={`${employee.position} - ${employee.organizational_unit_name || t('employees.organizationalUnit')}`}
          badge={
            <View style={styles.avatarLarge}>
              {employee.profile_image ? (
                <Image
                  source={{ uri: employee.profile_image }}
                  style={styles.avatarImage}
                  resizeMode="cover"
                />
              ) : (
                <UserAvatarIcon size={56} />
              )}
            </View>
          }
          actions={
            <Button
              variant="secondary"
              size="sm"
              onPress={handleEdit}
              leadingIcon={<EditIcon size={14} color={tokens.colors.semantic.general.foreground} />}
            >
              {t('employees.editData')}
            </Button>
          }
        />

        {/* Row 1: Three Info Cards */}
        <View style={[styles.cardsRow, isSmallScreen && styles.cardsRowSmall]}>
          {/* Card 1: المعلومات الشخصية */}
          <View style={[styles.card, isSmallScreen && styles.cardFullWidth]}>
            <Text style={styles.cardTitleSimple}>{t('employees.personalInfo')}</Text>
            <View style={styles.cardBodySimple}>
              <InfoRow label={t('employees.name')} value={employee.name_ar} />
              <InfoRow label={t('employees.emailLabel')} value={employee.email || '-'} />
              <InfoRow label={t('employees.phoneLabel')} value={employee.phone || '-'} />
              <InfoRow label={t('employees.birthDate')} value={employee.birth_date ? formatArabicDate(employee.birth_date) : '-'} />
              <InfoRow label={t('employees.genderLabel')} value={employee.gender_display || (employee.gender === 'female' ? t('employees.female') : t('employees.male'))} />
            </View>
          </View>

          {/* Card 2: الملف التعريفي */}
          <View style={[styles.card, isSmallScreen && styles.cardFullWidth]}>
            <Text style={styles.cardTitleSimple}>{t('employees.profileSection')}</Text>
            <View style={styles.cardBodySimple}>
              <InfoRow label={t('employees.organizationLabel')} value={employee.department_name || '-'} />
              <InfoRow label={t('employees.organizationalUnit')} value={employee.organizational_unit_name || '-'} />
              <InfoRow label={t('employees.type')} value={t('employees.employee')} />
              <InfoRow label={t('employees.jobTitle')} value={employee.position || t('employees.adminEmployee')} />
              <InfoRow label={t('employees.registrationDate')} value={employee.created_at ? new Date(employee.created_at).toLocaleDateString('ar-SA') : '-'} />
            </View>
          </View>

          {/* Card 3: إحصائيات الأداء */}
          <View style={[styles.card, isSmallScreen && styles.cardFullWidth]}>
            <Text style={styles.cardTitleSimple}>{t('employees.performanceStats')}</Text>
            <View style={styles.statsCardLayout}>
              {/* Left: Circular Progress */}
              <View style={styles.statsCircleContainer}>
                <CircularProgress percentage={overallPercentage} size={90} showLabel={false} />
                <Text style={styles.statsCirclePercent}>{overallPercentage} %</Text>
                <Text style={styles.statsCircleLabel}>{t('employees.overallPerformance')}</Text>
              </View>
              {/* Right: Stats Table */}
              <View style={styles.statsTableContainer}>
                <InfoRow label={t('employees.kpisCount')} value={kpis.length.toString()} />
                <InfoRow label={t('employees.readingsCount')} value={totalReadings.toString()} />
                <InfoRow label={t('employees.lastReading')} value={lastReading} />
              </View>
            </View>
          </View>
        </View>

        {/* Row 2: KPI Section with Trend Chart */}
        <View style={[styles.kpiTrendRow, isSmallScreen && styles.kpiTrendRowSmall]}>
          {/* Left: اتجاه المؤشر - Trend Chart */}
          <View style={[styles.trendChartColumn, isSmallScreen && styles.trendChartColumnSmall]}>
            <View style={[styles.sideCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.sideCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.kpiTrend')}</Text>
              <View style={styles.chartWrapper}>
                {/* Chart Grid Lines */}
                <View style={styles.chartGridContainer}>
                  <View style={styles.chartGridRow}>
                    <Text style={styles.chartYLabel}>100%</Text>
                    <View style={styles.chartGridLine} />
                  </View>
                  <View style={styles.chartGridRow}>
                    <Text style={styles.chartYLabel}>50%</Text>
                    <View style={styles.chartGridLine} />
                  </View>
                  <View style={styles.chartGridRow}>
                    <Text style={styles.chartYLabel}>0%</Text>
                    <View style={styles.chartGridLine} />
                  </View>
                </View>
                {/* Chart Line */}
                <Svg width="100%" height={100} style={styles.chartSvg}>
                  <Path
                    d="M 20 30 Q 60 25, 100 20 T 180 15"
                    stroke={tokens.colors.raw.green["500"]}
                    strokeWidth={2}
                    fill="transparent"
                  />
                </Svg>
                {/* X-Axis Labels */}
                <View style={styles.chartXLabels}>
                  <Text style={styles.chartXLabel}>01-2025</Text>
                  <Text style={styles.chartXLabel}>02-2025</Text>
                  <Text style={styles.chartXLabel}>03-2025</Text>
                  <Text style={styles.chartXLabel}>04-2025</Text>
                </View>
                {/* Legend */}
                <View style={styles.chartLegend}>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: tokens.colors.raw.green["500"] }]} />
                    <Text style={styles.legendText}>{t('kpis.actualValue')}</Text>
                  </View>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: tokens.colors.raw.blue["500"] }]} />
                    <Text style={styles.legendText}>{t('kpis.target')}</Text>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Right: مؤشرات الأداء Section */}
          <View style={[styles.kpiSectionColumn, isSmallScreen && styles.kpiSectionColumnSmall]}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionTitleRow}>
                  <Text style={styles.sectionTitle}>{t('kpis.performanceIndicators')}</Text>
                  <View style={styles.percentageBadge}>
                    <SmallCircularProgress percentage={overallPercentage} size={32} />
                    <Text style={styles.percentageBadgeText}>%{overallPercentage}</Text>
                  </View>
                </View>
                <TouchableOpacity
                  style={styles.addKpiButton}
                  onPress={() => navigation.navigate('AddEmployee', { employeeId: id })}
                >
                  <PlusIcon size={14} color={tokens.colors.semantic.general.foreground} />
                  <Text style={styles.addKpiButtonText}>{t('kpis.addIndicator')}</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.kpiCardsBackground}>
                {kpisLoading ? (
                  <View style={styles.kpiLoadingContainer}>
                    <ActivityIndicator size="small" color={COLORS.primary} />
                    <Text style={styles.kpiLoadingText}>{t('kpis.loadingKPIs')}</Text>
                  </View>
                ) : kpis.length === 0 ? (
                  <View style={styles.noKpisContainer}>
                    <Text style={styles.noKpisText}>{t('employees.noKpisForEmployee')}</Text>
                  </View>
                ) : (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.kpiCardsScrollContent}
                    style={styles.kpiCardsScrollView}
                  >
                    {kpis.map((kpi) => {
                      const achievement = parseFloat(String(kpi.achievement_percentage ?? '0')) || 0;
                      // Color based on achievement: green >= 80%, yellow >= 50%, red < 50%
                      // (same thresholds as Process/Service/UnitGoal detail KPI cards)
                      const progressColor =
                        achievement >= 80
                          ? '#2CA02C'
                          : achievement >= 50
                            ? tokens.colors.raw.yellow["500"]
                            : tokens.colors.raw.red["500"];

                      return (
                        <View key={kpi.id} style={[styles.kpiCardWrapper, { width: horizontalCardWidth }]}>
                          <TargetAchievementCard
                            title={kpi.name}
                            achieved={kpi.latest_value || '0'}
                            target={kpi.target_value}
                            unit={kpi.unit}
                            achievedColor={progressColor}
                            percentage={achievement}
                            onViewDetails={() => navigation.navigate('KPIDetail', { id: kpi.id })}
                          />
                        </View>
                      );
                    })}
                  </ScrollView>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Row 3: العمليات الرئيسية Section (Full Width) - Shows processes where employee is assigned to steps */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleRow}>
              <Text style={styles.sectionTitle}>{t('processes.mainProcesses')}</Text>
              <View style={styles.percentageBadge}>
                <SmallCircularProgress percentage={parseFloat((processes.reduce((sum, p) => sum + (p.achievement_percentage || 0), 0) / (processes.length || 1)).toFixed(2))} size={32} />
                <Text style={styles.percentageBadgeText}>%{formatPct(processes.reduce((sum, p) => sum + (p.achievement_percentage || 0), 0) / (processes.length || 1))}</Text>
              </View>
            </View>
            <View style={{ width: 100 }} />
          </View>
          <View style={styles.kpiCardsBackground}>
            <View style={[styles.processCardsContainer, isSmallScreen && styles.processCardsContainerSmall]}>
              {processesLoading ? (
                <View style={styles.kpiLoadingContainer}>
                  <ActivityIndicator size="small" color={COLORS.primary} />
                  <Text style={styles.kpiLoadingText}>{t('employees.loadingProcesses')}</Text>
                </View>
              ) : processes.length === 0 ? (
                <View style={styles.noKpisContainer}>
                  <Text style={styles.noKpisText}>{t('employees.noProcessesAssigned')}</Text>
                </View>
              ) : (
                processes.map((process) => (
                  <ProcessCard
                    key={process.id}
                    name={process.name}
                    percentage={process.achievement_percentage || 0}
                    onViewDetails={() => navigation.navigate('ProcessDetail', { id: process.id })}
                    viewDetailsText={t('common.viewDetails')}
                  />
                ))
              )}
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={deleteModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setDeleteModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('common.confirmDelete')}</Text>
            <Text style={styles.modalMessage}>
              {t('employees.confirmDeleteEmployee')} "{employee?.name_ar}"?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setDeleteModalVisible(false)}
              >
                <Text style={styles.modalCancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalDeleteButton, deleting && styles.modalButtonDisabled]}
                onPress={confirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color={tokens.colors.raw.white} />
                ) : (
                  <Text style={styles.modalDeleteButtonText}>{t('common.delete')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["50"],
  },
  contentContainer: {
    padding: tokens.spacing.absolute["4"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["5"],
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.raw.gray["500"],
    marginBottom: tokens.spacing.absolute["4"],
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
  retryButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  retryButtonText: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.regular,
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.gray["400"],
    fontFamily: FONTS.regular,
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.green["500"],
    fontFamily: FONTS.medium,
  },
  avatarLarge: {
    width: 52,
    height: 52,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: tokens.colors.raw.gray["100"],
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 52,
    height: 52,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  // Main Content Layout
  mainContent: {
    flexDirection: 'row-reverse', // RTL: main content on right, side panel on left
    gap: tokens.spacing.absolute["4"],
  },
  mainContentSmall: {
    flexDirection: 'column-reverse',
  },
  rightColumn: {
    flex: 1,
  },
  rightColumnSmall: {
    flex: undefined,
  },
  leftColumn: {
    width: 280,
  },
  leftColumnSmall: {
    width: '100%',
    marginBottom: tokens.spacing.absolute["4"],
  },
  // KPI and Trend Chart Row
  kpiTrendRow: {
    flexDirection: 'row', // Trend chart on left, KPIs on right
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  kpiTrendRowSmall: {
    flexDirection: 'column',
  },
  trendChartColumn: {
    width: 280,
  },
  trendChartColumnSmall: {
    width: '100%',
    marginBottom: tokens.spacing.absolute["4"],
  },
  kpiSectionColumn: {
    flex: 1,
  },
  kpiSectionColumnSmall: {
    flex: undefined,
  },
  // Cards Row
  cardsRow: {
    flexDirection: 'row-reverse', // RTL: cards start from right
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  cardsRowSmall: {
    flexDirection: 'column',
  },
  card: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
  },
  cardFullWidth: {
    flex: undefined,
    marginBottom: tokens.spacing.absolute["3"],
  },
  cardHeader: {
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
  },
  cardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  cardTitleSimple: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  cardBody: {
    padding: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
  },
  cardBodySimple: {
    gap: tokens.spacing.absolute["4"],
  },
  statsCardLayout: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
    marginTop: tokens.spacing.absolute["3"],
  },
  statsCircleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  statsCirclePercent: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["800"],
    marginTop: tokens.spacing.absolute["1"],
  },
  statsCircleLabel: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  statsTableContainer: {
    flex: 1,
    gap: tokens.spacing.absolute["3"],
  },
  cardBodyStats: {
    padding: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Info Row
  infoRow: {
    flexDirection: 'row-reverse', // RTL: label box on right, value on left
    alignItems: 'center',
  },
  infoLabelBox: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    paddingVertical: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-4"],
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
  infoValue: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    paddingHorizontal: tokens.spacing.absolute["2"],
    fontFamily: FONTS.regular,
  },
  // Stats Progress Row
  statsProgressRow: {
    flexDirection: 'row-reverse', // RTL: label box on right, progress on left
    alignItems: 'center',
  },
  statsProgressContent: {
    flex: 1,
    flexDirection: 'row-reverse', // RTL: progress on left, text on right
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  statsProgressValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["800"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  // Stats
  statsGrid: {
    flex: 1,
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
  },
  statLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  statValue: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.gray["800"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  circularProgressWrapper: {
    marginStart: tokens.spacing.absolute["4"],
  },
  // Section
  section: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: 'row-reverse', // RTL: title on right, button on left
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  sectionTitleRow: {
    flexDirection: 'row-reverse', // RTL: title before badge
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.medium,
  },
  percentageBadge: {
    backgroundColor: '#DCFCE7',
    paddingVertical: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-20"],
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  percentageBadgeIcon: {
    width: 16,
    height: 16,
  },
  percentageBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.green["500"],
    fontFamily: FONTS.medium,
  },
  countBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingVertical: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-12"],
  },
  countBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["500"],
    fontFamily: FONTS.medium,
  },
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.white,
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  // KPI Cards - Horizontal Scroll RTL
  kpiCardsScrollView: {
    // RTL support - flip the scroll view horizontally so it scrolls from right to left
    transform: [{ scaleX: -1 }],
  },
  kpiCardsScrollContent: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["2"],
  },
  kpiCardWrapper: {
    // Flip card back to normal orientation
    transform: [{ scaleX: -1 }],
  },
  kpiCardsBackground: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
  },
  kpiLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.absolute["5"],
    gap: tokens.spacing.absolute["2_5"],
  },
  kpiLoadingText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.regular,
  },
  noKpisContainer: {
    alignItems: 'flex-end',
    padding: tokens.spacing.absolute["5"],
    width: '100%',
  },
  noKpisText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  // Progress Bar - RTL (fills from right to left)
  progressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-4"],
    position: 'relative',
    flexDirection: 'row-reverse', // RTL: start from right
  },
  progressBarFill: {
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  progressDot: {
    position: 'absolute',
    top: -3,
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    marginEnd: -7, // RTL: adjusted for right-to-left
    borderWidth: 2,
    borderColor: tokens.colors.raw.white,
  },
  // Process Cards
  processCardsContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
  },
  processCardsContainerSmall: {
    flexDirection: 'column',
  },
  processCard: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["4"],
  },
  processCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    fontFamily: FONTS.medium,
  },
  kpiProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  kpiPercentageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  kpiPercentage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: tokens.colors.raw.green["500"],
  },
  kpiDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
  },
  kpiDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  processCardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // Side Cards
  sideCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  sideCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
    fontFamily: FONTS.medium,
  },
  // Chart
  chartWrapper: {
    paddingTop: tokens.spacing.absolute["2"],
  },
  chartGridContainer: {
    gap: 30,
  },
  chartGridRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  chartYLabel: {
    fontSize: 10,
    color: tokens.colors.raw.gray["400"],
    width: 35,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  chartGridLine: {
    flex: 1,
    height: 1,
    backgroundColor: tokens.colors.raw.slate["200"],
  },
  chartSvg: {
    position: 'absolute',
    top: 20,
    start: 40,
    end: 0,
  },
  chartXLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: tokens.spacing.absolute["3"],
    paddingStart: tokens.spacing.absolute["10"],
  },
  chartXLabel: {
    fontSize: 9,
    color: tokens.colors.raw.gray["400"],
    fontFamily: FONTS.regular,
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["5"],
    marginTop: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.slate["100"],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  legendText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.slate["500"],
    fontFamily: FONTS.regular,
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-16"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    color: tokens.colors.raw.gray["800"],
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["3"],
    fontFamily: FONTS.medium,
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["6"],
    lineHeight: 22,
    fontFamily: FONTS.regular,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.gray["100"],
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.medium,
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.red["500"],
    alignItems: 'center',
  },
  modalButtonDisabled: {
    opacity: 0.7,
  },
  modalDeleteButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
});

const styles = getStyles(true);
